// Document theme fonts (word/theme/theme1.xml).
//
// Word's default template writes NO explicit w:rFonts/@w:ascii anywhere. Every
// run's font resolves through a THEME TOKEN — `w:rFonts/@w:asciiTheme="minorHAnsi"`
// on docDefaults — into the theme's font scheme:
//
//   a:theme / a:themeElements / a:fontScheme / a:majorFont | a:minorFont / a:latin@typeface
//
// major = headings, minor = body. Without this lookup a stock Word document
// reports no font at all and every run silently inherits the editor's own
// default face.
import { childByLocalName, elementChildren } from './readDocxXml';
import { THEME_FONT_SLOT } from './constants';
import type { OoxmlThemeFonts } from './types';

// The a:latin typeface of a a:majorFont / a:minorFont element. Empty typefaces
// ("" means "inherit the host application's default") are treated as absent.
function latinTypeface(fontEl: Element | null): string | undefined {
    const latin = childByLocalName(fontEl, 'latin');
    if (!latin) return undefined;
    for (const attr of Array.from(latin.attributes)) {
        if (attr.localName === 'typeface') return attr.value.trim() || undefined;
    }
    return undefined;
}

export function parseTheme(themeDoc: Document | null): OoxmlThemeFonts {
    const root = themeDoc?.documentElement ?? null;
    if (!root) return {};
    // a:fontScheme sits under a:themeElements, but tolerate a flatter shape by
    // scanning the element tree rather than hard-coding the path depth.
    let fontScheme: Element | null = null;
    const walk = (el: Element): void => {
        if (fontScheme) return;
        for (const child of elementChildren(el)) {
            if (child.localName === 'fontScheme') {
                fontScheme = child;
                return;
            }
            walk(child);
            if (fontScheme) return;
        }
    };
    walk(root);
    if (!fontScheme) return {};

    return {
        major: latinTypeface(childByLocalName(fontScheme, 'majorFont')),
        minor: latinTypeface(childByLocalName(fontScheme, 'minorFont')),
    };
}

/**
 * Resolve an `@w:asciiTheme` / `@w:hAnsiTheme` token to a concrete typeface.
 * Tokens are `majorHAnsi`/`majorAscii`/`majorBidi`/`majorEastAsia` and the
 * `minor*` equivalents; only the latin (HAnsi/Ascii) slots map onto a CSS
 * font-family, so east-asian and bidi tokens deliberately resolve to nothing.
 */
export function resolveThemeFont(token: string | null, theme: OoxmlThemeFonts): string | undefined {
    if (!token) return undefined;
    if (token === THEME_FONT_SLOT.MAJOR_ASCII || token === THEME_FONT_SLOT.MAJOR_HANSI) return theme.major;
    if (token === THEME_FONT_SLOT.MINOR_ASCII || token === THEME_FONT_SLOT.MINOR_HANSI) return theme.minor;
    return undefined;
}
